import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MainHeader from '../Components/MainHeader';
import {
  addLabData,
  addCreatinine, addBUN, addEGFR,
  addWBC, addRBC, addHemoglobin, addHematocrit, addPlatelet,
  addHbA1c,
  addCholesterol, addHDL, addLDL, addTriglyceride,
  addSGOT, addSGPT, addAlkalinePhosphatase,
  addUricAcid
} from '../services/labService';

const HospitalFormContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;


const Content = styled.div`
  padding: 0;
  max-width: 100%;
  margin: 0;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: normal;
  color: white;
  background-color: #00BBD3;
  text-align: left;
  margin: 0 0 32px 0;
  padding: 16px 24px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  color: #00BBD3;
  font-size: 16px;
  cursor: pointer;
  padding: 8px 24px;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;

  &:hover {
    opacity: 0.7;
  }
`;

const FormSection = styled.div`
  padding: 0 24px;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: #666666;
  margin-bottom: 24px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.5;
`;

const CheckMark = styled.span`
  color: #00BBD3;
  font-size: 18px;
  font-weight: bold;
`;

const RedText = styled.span`
  color: #ff0000;
  font-weight: bold;
`;

const InputGroup = styled.div`
  margin-bottom: 24px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InputField = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }

  &::placeholder {
    color: #cccccc;
  }

  /* Hide "BE" prefix in date input for English locale */
  &[type="date"]::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
  }

  &[type="date"]::-webkit-datetime-edit-year-field:before {
    content: attr(data-year) !important;
  }

  &[type="date"] {
    position: relative;
  }
`;

const TimeInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const CheckboxSection = styled.div`
  margin-bottom: 32px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const CheckboxItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    transform: translateX(4px);
    background-color: rgba(0, 187, 211, 0.02);
    border-radius: 8px;
    padding: 16px 8px;
  }
`;

const CheckboxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  border: 2px solid #00BBD3;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.1);
    border-color: #009bb3;
  }
  
  &:checked {
    background-color: #00BBD3;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 12px;
    background-position: center;
    background-repeat: no-repeat;
    transform: scale(1.15);
    animation: checkPulse 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @keyframes checkPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0.4);
    }
    50% {
      transform: scale(1.2);
      box-shadow: 0 0 0 8px rgba(0, 187, 211, 0.2);
    }
    100% {
      transform: scale(1.15);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
    }
  }
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  color: #666666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  flex: 1;
  font-weight: bold;
`;

const SubFormContainer = styled.div`
  margin-left: 32px;
  background-color: white;
  border-radius: 12px;
  padding: ${props => props.show ? '20px' : '0 20px'};
  overflow: hidden;
  max-height: ${props => props.show ? '2000px' : '0'};
  opacity: ${props => props.show ? '1' : '0'};
  transform: translateY(${props => props.show ? '0' : '-10px'});
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: max-height, opacity, transform, padding;
  backface-visibility: hidden;
  transform-style: preserve-3d;
`;

const SubInputGroup = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubInputLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: normal;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  margin-bottom: 8px;
`;

const SubInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SubInputField = styled.input`
  width: 120px;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 20px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
  
  &::placeholder {
    color: #cccccc;
  }
`;

const UnitLabel = styled.span`
  font-size: 14px;
  color: #999999;
  font-family: 'Noto Sans Thai', sans-serif !important;
  white-space: nowrap;
  min-width: fit-content;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioButton = styled.input.attrs({ type: 'radio' })`
  width: 16px;
  height: 16px;
  border: 2px solid #00BBD3;
  border-radius: 50%;
  background-color: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
  
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.1);
    border-color: #009bb3;
  }
  
  &:checked {
    background-color: #00BBD3;
    background-image: radial-gradient(circle, white 30%, transparent 30%);
    transform: scale(1.2);
    animation: radioPulse 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  @keyframes radioPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0.4);
    }
    50% {
      transform: scale(1.3);
      box-shadow: 0 0 0 6px rgba(0, 187, 211, 0.2);
    }
    100% {
      transform: scale(1.2);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
    }
  }
`;

const RadioLabel = styled.label`
  font-size: 16px;
  color: #666666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SaveButton = styled.button`
  background-color: #00BBD3;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 18px 32px;
  font-size: 18px;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);
  margin: 20px 0;

  &:hover {
    background-color: #009bb3;
  }

  &:active {
    background-color: #008aa0;
  }
`;

const HospitalCheckupForm = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    hospital: '',
    checkedItems: {
      kidney: false,
      bloodSugar: false,
      cholesterol: false,
      liver: false,
      blood: false,
      uricAcid: false,
      gout: false
    },
    testValues: {
      kidney: {
        creatinine: '',
        bun: '',
        egfr: ''
      },
      bloodSugar: {
        nonNpo: '',
        fasting: '',
        hba1c: ''
      },
      cholesterol: {
        hdl: '',
        ldl: '',
        cholesterol: '',
        triglyceride: ''
      },
      liver: {
        sgpt: '',
        sgot: '',
        alp: '',
        bilirubin: '',
        albumin: '',
        protein: '',
        ggt: ''
      },
      blood: {
        rbc: '',
        hemoglobin: '',
        hematocrit: '',
        wbc: '',
        platelets: '',
        mcv: '',
        mch: '',
        mchc: ''
      },
      uricAcid: {
        color: '',
        clarity: '',
        ph: '',
        specificGravity: '',
        protein: '',
        glucose: '',
        ketones: '',
        blood: '',
        leukocytes: '',
        nitrite: '',
        bilirubin: '',
        urobilinogen: '',
        redBloodCells: '',
        whiteBloodCells: ''
      },
      gout: {
        uricAcid: ''
      }
    }
  });


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (item) => {
    setFormData(prev => ({
      ...prev,
      checkedItems: {
        ...prev.checkedItems,
        [item]: !prev.checkedItems[item]
      }
    }));
  };

  const handleTestValueChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      testValues: {
        ...prev.testValues,
        [category]: {
          ...prev.testValues[category],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    if (isSaving) return; // ป้องกันการคลิกซ้ำ

    try {
      setIsSaving(true);

      // Create datetime from form data
      const datetime = `${formData.date} ${formData.time}:00`;

      // Save hospital (ตรวจที่) — default to MFU Wellness Center if not filled
      const hospitalName = formData.hospital?.trim() || 'MFU Wellness Center';
      await addLabData([{ dateadd: datetime, name: 'hospital', value: hospitalName }]);

      // Save Kidney data if checked
      if (formData.checkedItems.kidney) {
        const { creatinine, bun, egfr } = formData.testValues.kidney;

        if (creatinine) {
          await addCreatinine(creatinine, datetime);
        }

        if (bun) {
          await addBUN(bun, datetime);
        }

        if (egfr) {
          await addEGFR(egfr, datetime);
        }
      }

      // Save CBC (ความสมบูรณ์ของเลือด) data if checked
      if (formData.checkedItems.blood) {
        const { wbc, rbc, hemoglobin, hematocrit, platelets, mcv, mch, mchc } = formData.testValues.blood;

        if (wbc) {
          await addWBC(wbc, datetime);
        }

        if (rbc) {
          await addRBC(rbc, datetime);
        }

        if (hemoglobin) {
          await addHemoglobin(hemoglobin, datetime);
        }

        if (hematocrit) {
          await addHematocrit(hematocrit, datetime);
        }

        if (platelets) {
          await addPlatelet(platelets, datetime);
        }

        if (mcv) {
          await addLabData([{ dateadd: datetime, name: 'mcv', value: mcv.toString() }]);
        }

        if (mch) {
          await addLabData([{ dateadd: datetime, name: 'mch', value: mch.toString() }]);
        }

        if (mchc) {
          await addLabData([{ dateadd: datetime, name: 'mchc', value: mchc.toString() }]);
        }
      }

      // Save น้ำตาลในเลือด data if checked
      if (formData.checkedItems.bloodSugar) {
        const { nonNpo, fasting, hba1c } = formData.testValues.bloodSugar;

        // บันทึกด้วย name ที่ HbA1cPage ดึง (fbs/fpg, non_npo_glucose, hba1c)
        if (nonNpo) {
          await addLabData([{ dateadd: datetime, name: 'non_npo_glucose', value: nonNpo.toString() }]);
        }
        if (fasting) {
          await addLabData([{ dateadd: datetime, name: 'fbs/fpg', value: fasting.toString() }]);
        }
        if (hba1c) {
          await addLabData([{ dateadd: datetime, name: 'hba1c', value: hba1c.toString() }]);
        }
      }

      // Save Lipid (ไขมัน) data if checked
      if (formData.checkedItems.cholesterol) {
        const { cholesterol, hdl, ldl, triglyceride } = formData.testValues.cholesterol;

        if (cholesterol) {
          await addCholesterol(cholesterol, datetime);
        }

        if (hdl) {
          await addHDL(hdl, datetime);
        }

        if (ldl) {
          await addLDL(ldl, datetime);
        }

        if (triglyceride) {
          await addTriglyceride(triglyceride, datetime);
        }
      }

      // Save Liver (ตับ) data if checked
      if (formData.checkedItems.liver) {
        const { sgot, sgpt, alp, bilirubin, albumin, protein, ggt } = formData.testValues.liver;

        if (sgot) {
          await addSGOT(sgot, datetime);
        }

        if (sgpt) {
          await addSGPT(sgpt, datetime);
        }

        if (alp) {
          await addAlkalinePhosphatase(alp, datetime);
        }

        if (bilirubin) {
          await addLabData([{ dateadd: datetime, name: 'bilirubin', value: bilirubin.toString() }]);
        }

        if (albumin) {
          await addLabData([{ dateadd: datetime, name: 'albumin', value: albumin.toString() }]);
        }

        if (protein) {
          await addLabData([{ dateadd: datetime, name: 'protein', value: protein.toString() }]);
        }

        if (ggt) {
          await addLabData([{ dateadd: datetime, name: 'ggt', value: ggt.toString() }]);
        }
      }

      // Save Uric Acid (กรดยูริค) data if checked
      if (formData.checkedItems.gout) {
        const { uricAcid } = formData.testValues.gout;

        if (uricAcid) {
          await addUricAcid(uricAcid, datetime);
        }
      }

      // Save Urine Test data if checked
      if (formData.checkedItems.uricAcid) {
        const urineData = formData.testValues.uricAcid;

        // Save each urine parameter with correct DB field names
        if (urineData.color) {
          await addLabData([{ dateadd: datetime, name: 'urinalysiscolorresul', value: urineData.color.toString() }]);
        }
        if (urineData.clarity) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisapperancer', value: urineData.clarity.toString() }]);
        }
        if (urineData.urineAppearance) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisapperancer', value: urineData.urineAppearance.toString() }]);
        }
        if (urineData.ph) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisphresult', value: urineData.ph.toString() }]);
        }
        if (urineData.specificGravity) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisspecificgr', value: urineData.specificGravity.toString() }]);
        }
        if (urineData.protein) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisproteinres', value: urineData.protein.toString() }]);
        }
        if (urineData.glucose) {
          await addLabData([{ dateadd: datetime, name: 'urinalysissugarresul', value: urineData.glucose.toString() }]);
        }
        if (urineData.ketones) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisketoneresu', value: urineData.ketones.toString() }]);
        }
        if (urineData.blood) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisbloodresul', value: urineData.blood.toString() }]);
        }
        if (urineData.leukocytes) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisleukocyter', value: urineData.leukocytes.toString() }]);
        }
        if (urineData.nitrite) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisnitritere', value: urineData.nitrite.toString() }]);
        }
        if (urineData.bilirubin) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisbilirubinr', value: urineData.bilirubin.toString() }]);
        }
        if (urineData.urobilinogen) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisurobilino', value: urineData.urobilinogen.toString() }]);
        }
        if (urineData.redBloodCells) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisrbcresult', value: urineData.redBloodCells.toString() }]);
        }
        if (urineData.whiteBloodCells) {
          await addLabData([{ dateadd: datetime, name: 'urinalysiswbcresult', value: urineData.whiteBloodCells.toString() }]);
        }
        if (urineData.bacteria) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisbactcellre', value: urineData.bacteria.toString() }]);
        }
        if (urineData.epithelialCells) {
          await addLabData([{ dateadd: datetime, name: 'urinalysisepithelial', value: urineData.epithelialCells.toString() }]);
        }
      }

      // Save Stool Test data if checked (assuming there's a stool checkbox)
      if (formData.checkedItems.stool && formData.testValues.stool) {
        const stoolData = formData.testValues.stool;

        if (stoolData.stoolColor) {
          await addLabData([{ dateadd: datetime, name: 'stoolColor', value: stoolData.stoolColor.toString() }]);
        }
        if (stoolData.stoolConsistency) {
          await addLabData([{ dateadd: datetime, name: 'stoolConsistency', value: stoolData.stoolConsistency.toString() }]);
        }
        if (stoolData.stoolRBC) {
          await addLabData([{ dateadd: datetime, name: 'stoolRBC', value: stoolData.stoolRBC.toString() }]);
        }
        if (stoolData.stoolWBC) {
          await addLabData([{ dateadd: datetime, name: 'stoolWBC', value: stoolData.stoolWBC.toString() }]);
        }
        if (stoolData.stoolParasite) {
          await addLabData([{ dateadd: datetime, name: 'stoolParasite', value: stoolData.stoolParasite.toString() }]);
        }
        if (stoolData.stoolOccultBlood) {
          await addLabData([{ dateadd: datetime, name: 'stoolOccultBlood', value: stoolData.stoolOccultBlood.toString() }]);
        }
      }

      // Note: X-ray is not connected to API yet

      alert('บันทึกข้อมูลเรียบร้อยแล้ว');
      navigate('/dashboard');
    } catch (error) {
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <HospitalFormContainer>
      <MainHeader />

      <Content>
        <PageTitle>เพิ่มผลตรวจจากโรงพยาบาล</PageTitle>

        <BackButton onClick={() => navigate('/dashboard')}>
          ‹ ย้อนกลับ
        </BackButton>

        <FormSection>
          <SectionTitle>ตรวจจากโรงพยาบาล</SectionTitle>
          <SectionSubtitle>
            ท่านสามารถกด <CheckMark>✓</CheckMark> ที่ช่องเพื่อกรอกข้อมูลเฉพาะที่ต้องการบันทึกผลตรวจ{' '}
            <RedText>(ไม่ต้องกรอกทั้งหมด)</RedText>
          </SectionSubtitle>

          <InputGroup>
            <InputLabel>วันที่ตรวจ</InputLabel>
            <InputField
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              style={{
                colorScheme: 'light',
                position: 'relative'
              }}
              pattern="\d{1,2}/\d{1,2}/\d{4}"
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>เวลาที่ตรวจ</InputLabel>
            <TimeInput
              type="time"
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>ตรวจที่</InputLabel>
            <InputField
              type="text"
              placeholder="ตรวจที่"
              value={formData.hospital}
              onChange={(e) => handleInputChange('hospital', e.target.value)}
            />
          </InputGroup>

          <CheckboxSection>
            <CheckboxGrid>
              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="kidney"
                    checked={formData.checkedItems.kidney}
                    onChange={() => handleCheckboxChange('kidney')}
                  />
                  <CheckboxLabel htmlFor="kidney">การทำงานของไต</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.kidney}>
                  <SubInputGroup>
                    <SubInputLabel>Creatinine</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Creatinine"
                        value={formData.testValues.kidney.creatinine}
                        onChange={(e) => handleTestValueChange('kidney', 'creatinine', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>BUN , Urea (Blood Urea Nitrogen)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="BUN , Urea"
                        value={formData.testValues.kidney.bun}
                        onChange={(e) => handleTestValueChange('kidney', 'bun', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>eGFR</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="eGFR"
                        value={formData.testValues.kidney.egfr}
                        onChange={(e) => handleTestValueChange('kidney', 'egfr', e.target.value)}
                      />
                      <UnitLabel>mL/min/1.73m²</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="bloodSugar"
                    checked={formData.checkedItems.bloodSugar}
                    onChange={() => handleCheckboxChange('bloodSugar')}
                  />
                  <CheckboxLabel htmlFor="bloodSugar">น้ำตาลในเลือด</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.bloodSugar}>
                  <SubInputGroup>
                    <SubInputLabel>น้ำตาลในเลือด (Non-NPO blood glucose)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="Non-NPO"
                        value={formData.testValues.bloodSugar.nonNpo}
                        onChange={(e) => handleTestValueChange('bloodSugar', 'nonNpo', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>น้ำตาลอดอาหาร (Fasting blood glucose)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="Fasting"
                        value={formData.testValues.bloodSugar.fasting}
                        onChange={(e) => handleTestValueChange('bloodSugar', 'fasting', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>HbA1C</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="HbA1C"
                        value={formData.testValues.bloodSugar.hba1c}
                        onChange={(e) => handleTestValueChange('bloodSugar', 'hba1c', e.target.value)}
                      />
                      <UnitLabel>%</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="cholesterol"
                    checked={formData.checkedItems.cholesterol}
                    onChange={() => handleCheckboxChange('cholesterol')}
                  />
                  <CheckboxLabel htmlFor="cholesterol">ไขมันในเลือด</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.cholesterol}>
                  <SubInputGroup>
                    <SubInputLabel>Cholesterol (คอเลสเตอรอลรวม)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Cholesterol"
                        value={formData.testValues.cholesterol.cholesterol}
                        onChange={(e) => handleTestValueChange('cholesterol', 'cholesterol', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>Triglyceride (ไตรกลีเซอไรด์)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Triglyceride"
                        value={formData.testValues.cholesterol.triglyceride}
                        onChange={(e) => handleTestValueChange('cholesterol', 'triglyceride', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>HDL</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="HDL"
                        value={formData.testValues.cholesterol.hdl}
                        onChange={(e) => handleTestValueChange('cholesterol', 'hdl', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>LDL</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="LDL"
                        value={formData.testValues.cholesterol.ldl}
                        onChange={(e) => handleTestValueChange('cholesterol', 'ldl', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="liver"
                    checked={formData.checkedItems.liver}
                    onChange={() => handleCheckboxChange('liver')}
                  />
                  <CheckboxLabel htmlFor="liver">การทำงานของตับ</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.liver}>
                  <SubInputGroup>
                    <SubInputLabel>SGPT (ALT)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="SGPT"
                        value={formData.testValues.liver.sgpt}
                        onChange={(e) => handleTestValueChange('liver', 'sgpt', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>SGOT (AST)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="SGOT"
                        value={formData.testValues.liver.sgot}
                        onChange={(e) => handleTestValueChange('liver', 'sgot', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ALP (Alkaline Phosphatase)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ALP"
                        value={formData.testValues.liver.alp}
                        onChange={(e) => handleTestValueChange('liver', 'alp', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>Total Bilirubin</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Total Bilirubin"
                        value={formData.testValues.liver.bilirubin}
                        onChange={(e) => handleTestValueChange('liver', 'bilirubin', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>อัลบูมิน (Albumin)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="อัลบูมิน"
                        value={formData.testValues.liver.albumin}
                        onChange={(e) => handleTestValueChange('liver', 'albumin', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>โปรตีนรวม (Total Protein)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="โปรตีนรวม"
                        value={formData.testValues.liver.protein}
                        onChange={(e) => handleTestValueChange('liver', 'protein', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>GGT (Gamma-glutamyl transferase)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="GGT"
                        value={formData.testValues.liver.ggt}
                        onChange={(e) => handleTestValueChange('liver', 'ggt', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>Globulin</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Globulin"
                        value={formData.testValues.liver.globulin || ''}
                        onChange={(e) => handleTestValueChange('liver', 'globulin', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>Direct Bilirubin</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Direct Bilirubin"
                        value={formData.testValues.liver.directBilirubin || ''}
                        onChange={(e) => handleTestValueChange('liver', 'directBilirubin', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="blood"
                    checked={formData.checkedItems.blood}
                    onChange={() => handleCheckboxChange('blood')}
                  />
                  <CheckboxLabel htmlFor="blood">ความสมบูรณ์ของเลือด</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.blood}>
                  <SubInputGroup>
                    <SubInputLabel>จำนวนเม็ดเลือดแดง (RBC)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="เม็ดเลือดแดง"
                        value={formData.testValues.blood.rbc}
                        onChange={(e) => handleTestValueChange('blood', 'rbc', e.target.value)}
                      />
                      <UnitLabel>million cells/μL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ฮีโมโกลบิน (Hemoglobin, Hb)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ฮีโมโกลบิน"
                        value={formData.testValues.blood.hemoglobin}
                        onChange={(e) => handleTestValueChange('blood', 'hemoglobin', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ฮีมาโตคริต (Hematocrit, Hct)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ฮีมาโตคริต"
                        value={formData.testValues.blood.hematocrit}
                        onChange={(e) => handleTestValueChange('blood', 'hematocrit', e.target.value)}
                      />
                      <UnitLabel>%</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>จำนวนเม็ดเลือดขาว (WBC)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="เม็ดเลือดขาว"
                        value={formData.testValues.blood.wbc}
                        onChange={(e) => handleTestValueChange('blood', 'wbc', e.target.value)}
                      />
                      <UnitLabel>cells/μL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>เกล็ดเลือด (Platelets)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="เกล็ดเลือด"
                        value={formData.testValues.blood.platelets}
                        onChange={(e) => handleTestValueChange('blood', 'platelets', e.target.value)}
                      />
                      <UnitLabel>cells/μL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>MCV</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="MCV"
                        value={formData.testValues.blood.mcv}
                        onChange={(e) => handleTestValueChange('blood', 'mcv', e.target.value)}
                      />
                      <UnitLabel>fL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>MCH</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="MCH"
                        value={formData.testValues.blood.mch}
                        onChange={(e) => handleTestValueChange('blood', 'mch', e.target.value)}
                      />
                      <UnitLabel>pg/cell</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>MCHC</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="MCHC"
                        value={formData.testValues.blood.mchc}
                        onChange={(e) => handleTestValueChange('blood', 'mchc', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="uricAcid"
                    checked={formData.checkedItems.uricAcid}
                    onChange={() => handleCheckboxChange('uricAcid')}
                  />
                  <CheckboxLabel htmlFor="uricAcid">ผลการตรวจปัสสาวะ</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.uricAcid}>
                  {/* 1. สีของปัสสาวะ */}
                  <SubInputGroup>
                    <SubInputLabel>1. สีของปัสสาวะ (Color)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="color-yellow"
                          name="urineColor"
                          value="Yellow"
                          checked={formData.testValues.uricAcid.color === 'Yellow'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'color', e.target.value)}
                        />
                        <RadioLabel htmlFor="color-yellow">Yellow (ปกติ)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="color-abnormal"
                          name="urineColor"
                          value="abnormal"
                          checked={formData.testValues.uricAcid.color === 'abnormal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'color', e.target.value)}
                        />
                        <RadioLabel htmlFor="color-abnormal">ผิดปกติ</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  {/* 2. ความใส */}
                  <SubInputGroup>
                    <SubInputLabel>2. ความใส (Clarity)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="clarity-clear"
                          name="urineClarity"
                          value="Clear"
                          checked={formData.testValues.uricAcid.clarity === 'Clear'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'clarity', e.target.value)}
                        />
                        <RadioLabel htmlFor="clarity-clear">Clear</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="clarity-abnormal"
                          name="urineClarity"
                          value="abnormal"
                          checked={formData.testValues.uricAcid.clarity === 'abnormal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'clarity', e.target.value)}
                        />
                        <RadioLabel htmlFor="clarity-abnormal">ผิดปกติ</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  {/* 3. pH */}
                  <SubInputGroup>
                    <SubInputLabel>3. pH (ค่า pH)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="กรอกค่า pH"
                        value={formData.testValues.uricAcid.ph}
                        onChange={(e) => handleTestValueChange('uricAcid', 'ph', e.target.value)}
                      />
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 4. ความถ่วงจำเพาะ */}
                  <SubInputGroup>
                    <SubInputLabel>4. ความถ่วงจำเพาะ (Specific Gravity)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.001"
                        placeholder="กรอกค่า (ทศนิยม 3 ตำแหน่ง)"
                        value={formData.testValues.uricAcid.specificGravity}
                        onChange={(e) => handleTestValueChange('uricAcid', 'specificGravity', e.target.value)}
                      />
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 5. โปรตีน */}
                  <SubInputGroup>
                    <SubInputLabel>5. โปรตีน (Protein)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.protein || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'protein', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 6. น้ำตาล */}
                  <SubInputGroup>
                    <SubInputLabel>6. น้ำตาล (Sugar)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.glucose || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'glucose', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 7. คีโตน */}
                  <SubInputGroup>
                    <SubInputLabel>7. คีโตน (Ketones)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.ketones || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'ketones', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 8. เลือด */}
                  <SubInputGroup>
                    <SubInputLabel>8. เลือด (Blood)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.blood || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'blood', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 9. เม็ดเลือดขาว */}
                  <SubInputGroup>
                    <SubInputLabel>9. เม็ดเลือดขาว (Leukocytes)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.leukocytes || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'leukocytes', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 10. ไนเตรต */}
                  <SubInputGroup>
                    <SubInputLabel>10. ไนเตรต (Nitrite)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.nitrite || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'nitrite', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 11. บิลิรูบิน */}
                  <SubInputGroup>
                    <SubInputLabel>11. บิลิรูบิน (Bilirubin)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.bilirubin || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'bilirubin', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 12. ยูโรบิลิโนเจน */}
                  <SubInputGroup>
                    <SubInputLabel>12. ยูโรบิลิโนเจน (Urobilinogen)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.urobilinogen || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'urobilinogen', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 13. เซลล์เม็ดเลือดแดง */}
                  <SubInputGroup>
                    <SubInputLabel>13. เซลล์เม็ดเลือดแดง (Red Blood Cells)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.redBloodCells || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'redBloodCells', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="0-1">0-1</option>
                        <option value="1-2">1-2</option>
                        <option value="2-3">2-3</option>
                        <option value="3-5">3-5</option>
                        <option value="5-10">5-10</option>
                        <option value="10-20">10-20</option>
                        <option value="20-30">20-30</option>
                        <option value="30-50">30-50</option>
                        <option value="50-100">50-100</option>
                        <option value="100-200">100-200</option>
                        <option value=">200">&gt;200</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 14. เซลล์เม็ดเลือดขาว */}
                  <SubInputGroup>
                    <SubInputLabel>14. เซลล์เม็ดเลือดขาว (White Blood Cells)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.whiteBloodCells || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'whiteBloodCells', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="0-1">0-1</option>
                        <option value="1-2">1-2</option>
                        <option value="2-3">2-3</option>
                        <option value="3-5">3-5</option>
                        <option value="5-10">5-10</option>
                        <option value="10-20">10-20</option>
                        <option value="20-30">20-30</option>
                        <option value="30-50">30-50</option>
                        <option value="50-100">50-100</option>
                        <option value="100-200">100-200</option>
                        <option value=">200">&gt;200</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 15. Urine Appearance */}
                  <SubInputGroup>
                    <SubInputLabel>15. Urine Appearance</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="urineAppearance-clear"
                          name="urineAppearance"
                          value="Clear"
                          checked={formData.testValues.uricAcid.urineAppearance === 'Clear'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'urineAppearance', e.target.value)}
                        />
                        <RadioLabel htmlFor="urineAppearance-clear">Clear</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="urineAppearance-abnormal"
                          name="urineAppearance"
                          value="abnormal"
                          checked={formData.testValues.uricAcid.urineAppearance === 'abnormal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'urineAppearance', e.target.value)}
                        />
                        <RadioLabel htmlFor="urineAppearance-abnormal">ผิดปกติ</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  {/* 16. แบคทีเรีย */}
                  <SubInputGroup>
                    <SubInputLabel>16. แบคทีเรีย (Bacteria)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.bacteria || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'bacteria', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="Negative">Negative</option>
                        <option value="Trace">Trace</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 17. เซลล์เยื่อบุผิว Mucous thread */}
                  <SubInputGroup>
                    <SubInputLabel>17. เซลล์เยื่อบุผิว (Mucous thread)</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.mucousThread || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'mucousThread', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="0-1">0-1</option>
                        <option value="1-2">1-2</option>
                        <option value="2-3">2-3</option>
                        <option value="3-5">3-5</option>
                        <option value="5-10">5-10</option>
                        <option value="10-20">10-20</option>
                        <option value="20-30">20-30</option>
                        <option value="30-50">30-50</option>
                        <option value="50-100">50-100</option>
                        <option value="100-200">100-200</option>
                        <option value=">200">&gt;200</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>

                  {/* 18. Epithelial cell */}
                  <SubInputGroup>
                    <SubInputLabel>18. Epithelial cell</SubInputLabel>
                    <SubInputWrapper>
                      <select
                        value={formData.testValues.uricAcid.epithelialCell || ''}
                        onChange={(e) => handleTestValueChange('uricAcid', 'epithelialCell', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', color: '#666', fontFamily: "'Noto Sans Thai', sans-serif" }}
                      >
                        <option value="">-- เลือก --</option>
                        <option value="0-1">0-1</option>
                        <option value="1-2">1-2</option>
                        <option value="2-3">2-3</option>
                        <option value="3-5">3-5</option>
                        <option value="5-10">5-10</option>
                        <option value="10-20">10-20</option>
                        <option value="20-30">20-30</option>
                        <option value="30-50">30-50</option>
                        <option value="50-100">50-100</option>
                        <option value="100-200">100-200</option>
                        <option value=">200">&gt;200</option>
                      </select>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="gout"
                    checked={formData.checkedItems.gout}
                    onChange={() => handleCheckboxChange('gout')}
                  />
                  <CheckboxLabel htmlFor="gout">กรดยูริค</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.gout}>
                  <SubInputGroup>
                    <SubInputLabel>กรดยูริค (Uric Acid)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="กรดยูริค"
                        value={formData.testValues.gout.uricAcid}
                        onChange={(e) => handleTestValueChange('gout', 'uricAcid', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>
            </CheckboxGrid>
          </CheckboxSection>

          <SaveButton onClick={handleSave} disabled={isSaving} style={{ opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}>
            {isSaving ? 'กำลังบันทึก...' : 'บันทึก / กลับสู่หน้าหลัก'}
          </SaveButton>
        </FormSection>
      </Content>
    </HospitalFormContainer>
  );
};

export default HospitalCheckupForm;